/**
 * countryTimezone.ts — map a country to its IANA timezone, compute the hour
 * offset between the band's home zone and a venue zone (so the day sheet can
 * show "CET · +1h vs CH"), and render a zone as `UTC+1` for the PDFs/viewer.
 * (2026-08-12; UTC+X formatting added 2026-11-04, LOT 1.)
 *
 * Why `UTC+X`: a tour manager reads the day sheet, not a tz database. The user
 * asked explicitly for `UTC+1` / `UTC+2` / `UTC-5` / `UTC+0` instead of
 * `Europe/Berlin` in the PDFs and the DaysheetViewer. Free text typed by the
 * user ("CET +1 — NB: …") is NEVER rewritten: it is returned verbatim.
 */

const COUNTRY_TO_TZ: Record<string, string> = {
  // Europe
  Switzerland: 'Europe/Zurich',
  Schweiz: 'Europe/Zurich',
  Suisse: 'Europe/Zurich',
  Svizzera: 'Europe/Zurich',
  'Schweiz/Suisse/Svizzera/Svizra': 'Europe/Zurich',
  France: 'Europe/Paris',
  Germany: 'Europe/Berlin',
  Deutschland: 'Europe/Berlin',
  Belgium: 'Europe/Brussels',
  Netherlands: 'Europe/Amsterdam',
  Luxembourg: 'Europe/Luxembourg',
  Austria: 'Europe/Vienna',
  Spain: 'Europe/Madrid',
  Italy: 'Europe/Rome',
  Portugal: 'Europe/Lisbon',
  Ireland: 'Europe/Dublin',
  Finland: 'Europe/Helsinki',
  Suomi: 'Europe/Helsinki',
  Greece: 'Europe/Athens',
  Ελλάς: 'Europe/Athens',
  'Czech Republic': 'Europe/Prague',
  Czechia: 'Europe/Prague',
  Česko: 'Europe/Prague',
  'United Kingdom': 'Europe/London',
  UK: 'Europe/London',
  'Great Britain': 'Europe/London',
  Poland: 'Europe/Warsaw',
  Sweden: 'Europe/Stockholm',
  Norway: 'Europe/Oslo',
  Denmark: 'Europe/Copenhagen',
  Iceland: 'Atlantic/Reykjavik',
  Hungary: 'Europe/Budapest',
  Romania: 'Europe/Bucharest',
  Bulgaria: 'Europe/Sofia',
  Croatia: 'Europe/Zagreb',
  Slovenia: 'Europe/Ljubljana',
  Slovakia: 'Europe/Bratislava',
  Serbia: 'Europe/Belgrade',
  Ukraine: 'Europe/Kiev',
  Turkey: 'Europe/Istanbul',
  Estonia: 'Europe/Tallinn',
  Latvia: 'Europe/Riga',
  Lithuania: 'Europe/Vilnius',
  Belarus: 'Europe/Minsk',
  Moldova: 'Europe/Chisinau',
  Russia: 'Europe/Moscow',
  'Russian Federation': 'Europe/Moscow',
  // Caucasus / Central Asia
  Georgia: 'Asia/Tbilisi',
  Kazakhstan: 'Asia/Almaty',
  Kyrgyzstan: 'Asia/Bishkek',
  // North America
  'United States': 'America/New_York',
  USA: 'America/New_York',
  'United States of America': 'America/New_York',
  Canada: 'America/Toronto',
  Mexico: 'America/Mexico_City',
  // Latin America
  Brazil: 'America/Sao_Paulo',
  Colombia: 'America/Bogota',
  Chile: 'America/Santiago',
  Peru: 'America/Lima',
  Bolivia: 'America/La_Paz',
  'Costa Rica': 'America/Costa_Rica',
  Argentina: 'America/Argentina/Buenos_Aires',
  // Asia
  Japan: 'Asia/Tokyo',
  China: 'Asia/Shanghai',
  India: 'Asia/Kolkata',
  'South Korea': 'Asia/Seoul',
  Korea: 'Asia/Seoul',
  Thailand: 'Asia/Bangkok',
  Singapore: 'Asia/Singapore',
  // Middle East
  'United Arab Emirates': 'Asia/Dubai',
  UAE: 'Asia/Dubai',
  Israel: 'Asia/Jerusalem',
  // Africa / Oceania
  'South Africa': 'Africa/Johannesburg',
  Australia: 'Australia/Sydney',
  'New Zealand': 'Pacific/Auckland',
};

/** Normalize a country string (lowercase, trim, strip accents). */
function norm(s: string): string {
  return (s || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z]/g, '');
}

const NORM_TZ: Record<string, string> = {};
for (const [country, tz] of Object.entries(COUNTRY_TO_TZ)) NORM_TZ[norm(country)] = tz;

/** Resolve the IANA timezone for a country name (null when unknown). */
export function countryToTimezone(country?: string | null): string | null {
  if (!country) return null;
  return NORM_TZ[norm(country)] ?? null;
}

/**
 * Reference instant used to read a zone's offset. A bare `YYYY-MM-DD` (the
 * shape of `tour_dates.date`) is pinned at 12:00:00Z — noon UTC never falls on
 * the wrong side of a DST switch, so the offset is the one the show lives in.
 */
function refInstant(refDate?: Date | string | null): Date {
  if (refDate instanceof Date) return Number.isNaN(refDate.getTime()) ? new Date() : refDate;
  if (typeof refDate === 'string') {
    const s = refDate.trim();
    const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
    if (iso) return new Date(Date.UTC(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]), 12, 0, 0));
    const d = new Date(s);
    if (!Number.isNaN(d.getTime())) return d;
  }
  return new Date();
}

/**
 * UTC offset of an IANA timezone in MINUTES at `refDate` (default: now),
 * DST-aware, computed through `Intl` (no dependency). Null when the zone is
 * unknown/invalid.
 */
function zoneUtcOffsetMinutes(
  tz: string | null | undefined,
  refDate?: Date | string | null,
): number | null {
  if (!tz) return null;
  try {
    const when = refInstant(refDate);
    const fmt = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      hour12: false,
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
    const parts = fmt.formatToParts(when);
    const get = (t: string) => parts.find((p) => p.type === t)?.value || '';
    const y = Number(get('year')), mo = Number(get('month')), d = Number(get('day'));
    const h = Number(get('hour')), mi = Number(get('minute')), s = Number(get('second'));
    if (Number.isNaN(y) || Number.isNaN(mo) || Number.isNaN(d)) return null;
    // The zoned wall clock, re-encoded as if it were UTC, minus the real UTC
    // instant = the zone's offset (e.g. +2h in Berlin in July).
    const candidate = Date.UTC(y, mo - 1, d, h, mi, s);
    const utc = Date.UTC(
      when.getUTCFullYear(), when.getUTCMonth(), when.getUTCDate(),
      when.getUTCHours(), when.getUTCMinutes(), when.getUTCSeconds(),
    );
    return Math.round((candidate - utc) / 60000);
  } catch {
    return null;
  }
}

/**
 * UTC offset (whole hours, incl. DST) of an IANA timezone at `refDate`
 * (default: now). Returns null when the zone is unknown/invalid.
 */
export function zoneUtcOffsetHours(
  tz: string | null | undefined,
  refDate?: Date | string | null,
): number | null {
  const minutes = zoneUtcOffsetMinutes(tz, refDate);
  if (minutes == null) return null;
  return Math.round(minutes / 60);
}

/** A pure IANA identifier : "Europe/Berlin", "America/New_York"… */
const IANA_ID = /^[A-Za-z_]+\/[A-Za-z_]+(?:\/[A-Za-z_]+)?$/;

/**
 * Render a timezone as `UTC+1` / `UTC+2` / `UTC+0` / `UTC-5` (and `UTC+5:30`
 * when the offset carries minutes, e.g. India).
 *
 * - empty → `''` (nothing printed in the PDF/viewer);
 * - a pure IANA id → its offset at `refDate` (the show's own date, so summer
 *   and winter are respected);
 * - anything else (user free text: "UTC+2", "CET", "CET +1 — NB: turn off auto
 *   tz", an id Intl rejects) → returned trim()'ed and UNTOUCHED.
 */
export function formatTimezoneUtc(
  tz?: string | null,
  refDate?: Date | string | null,
): string {
  if (!tz) return '';
  const s = String(tz).trim();
  if (!s) return '';
  if (!IANA_ID.test(s)) return s;
  const minutes = zoneUtcOffsetMinutes(s, refDate);
  if (minutes == null) return s;
  const sign = minutes < 0 ? '-' : '+';
  const abs = Math.abs(minutes);
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  return `UTC${sign}${h}${m ? `:${String(m).padStart(2, '0')}` : ''}`;
}

/** Signed hour difference (venueTz − bandTz), e.g. +1. Null when unknown. */
export function timezoneOffsetHours(
  bandTimezone: string | null | undefined,
  venueTimezone: string | null | undefined,
  refDate?: Date | string | null,
): number | null {
  const b = zoneUtcOffsetHours(bandTimezone, refDate);
  const v = zoneUtcOffsetHours(venueTimezone, refDate);
  if (b == null || v == null) return null;
  return v - b;
}

/** Human label, e.g. "CET · +1h vs home" or "+1h". */
export function timezoneOffsetLabel(
  bandTimezone: string | null | undefined,
  venueTimezone: string | null | undefined,
  refDate?: Date | string | null,
): string {
  const d = timezoneOffsetHours(bandTimezone, venueTimezone, refDate);
  if (d == null) return '';
  if (d === 0) return 'same time';
  return `${d > 0 ? '+' : ''}${d}h vs home`;
}
